import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; arrowLeftPx?: number } | null>(null);
  const [actualPosition, setActualPosition] = useState<TooltipProps['position']>(position);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const offsetPx = 10;
  const viewportPadding = 10;

  const computeCoords = useCallback(() => {
    const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const tt = tooltipRef.current?.getBoundingClientRect();
    const ttW = tt?.width ?? 220;
    const ttH = tt?.height ?? 48;

    let pos = position;
    if (position === 'top' && rect.top - offsetPx - ttH < viewportPadding) pos = 'bottom';
    if (position === 'bottom' && rect.bottom + offsetPx + ttH > window.innerHeight - viewportPadding) pos = 'top';
    if (position === 'left' && rect.left - offsetPx - ttW < viewportPadding) pos = 'right';
    if (position === 'right' && rect.right + offsetPx + ttW > window.innerWidth - viewportPadding) pos = 'left';
    setActualPosition(pos);

    let left = 0;
    let top = 0;
    if (pos === 'top') {
      left = centerX - ttW / 2;
      top = rect.top - offsetPx - ttH;
    } else if (pos === 'bottom') {
      left = centerX - ttW / 2;
      top = rect.bottom + offsetPx;
    } else if (pos === 'left') {
      left = rect.left - offsetPx - ttW;
      top = centerY - ttH / 2;
    } else {
      left = rect.right + offsetPx;
      top = centerY - ttH / 2;
    }

    const clampedLeft = clamp(left, viewportPadding, window.innerWidth - viewportPadding - ttW);
    const clampedTop = clamp(top, viewportPadding, window.innerHeight - viewportPadding - ttH);
    const arrowLeftPx = clamp(centerX - clampedLeft, 16, ttW - 16);
    setCoords({ top: clampedTop, left: clampedLeft, arrowLeftPx });
  }, [position]);

  const tooltipCallbackRef = useCallback((node: HTMLDivElement | null) => {
    tooltipRef.current = node;
    if (node) {
      computeCoords();
    }
  }, [computeCoords]);

  useEffect(() => {
    if (!isVisible) {
      setCoords(null);
      return;
    }

    computeCoords();

    const handler = () => computeCoords();
    window.addEventListener('scroll', handler, { capture: true, passive: true });
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', handler);
    };
  }, [isVisible, computeCoords]);

  // Escape hides the tooltip while shown (keyboard parity with mouse/touch dismissal).
  useEffect(() => {
    if (!isVisible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsVisible(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isVisible]);

  // A touch on the trigger shouldn't be immediately followed by a touchend that hides
  // the tooltip (1.4.13 — content triggered by hover/focus must stay visible until the
  // user dismisses it). Instead, keep it open until a touch lands outside the trigger.
  useEffect(() => {
    if (!isVisible) return;
    const onTouchStartOutside = (e: TouchEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      setIsVisible(false);
    };
    document.addEventListener('touchstart', onTouchStartOutside);
    return () => document.removeEventListener('touchstart', onTouchStartOutside);
  }, [isVisible]);

  return (
    <div
      className="tooltip-container"
      ref={containerRef}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
      onTouchStart={() => setIsVisible(true)}
    >
      {children}
      {isVisible && coords && createPortal(
        <div
          ref={tooltipCallbackRef}
          className={`tooltip tooltip--${actualPosition}`}
          style={{
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            ['--tooltip-arrow-left' as any]: coords.arrowLeftPx ? `${coords.arrowLeftPx}px` : undefined,
          }}
        >
          <div className="tooltip__content">
            {content}
          </div>
          <div className="tooltip__arrow" />
        </div>,
        document.body
      )}
    </div>
  );
};







